import React, { useState } from 'react';
import { corelCommunication } from '../lib/corel/communication';
import { PackagePlusIcon as PackagePlus, Loader2Icon as Loader2 } from './icons';
import { useConnectivity } from '../contexts/ConnectivityContext';

type SupplyCategory = {
  name: string;
  items: string[];
  color?: string;
};

type ServiceSupplies = {
  categories: Record<string, SupplyCategory>;
};

const supplies: Record<string, ServiceSupplies> = {
  'DTF TÊXTIL': {
    categories: {
      'FILME': {
        name: 'FILME',
        items: ['FILME 60cm'],
        color: 'bg-purple-500'
      },
      'TINTAS': {
        name: 'TINTAS',
        items: ['CYAN', 'MAGENTA', 'AMARELO', 'PRETO', 'BRANCO'],
        color: 'bg-blue-500'
      },
      'POLIAMIDA': {
        name: 'POLIAMIDA',
        items: ['PÓ TPU'],
        color: 'bg-amber-500'
      }
    }
  },
  'DTF UV': {
    categories: {
      'FILME': {
        name: 'FILME',
        items: ['FILME A 60cm', 'FILME B 60cm'],
        color: 'bg-purple-500'
      },
      'TINTAS': {
        name: 'TINTAS',
        items: ['CYAN', 'MAGENTA', 'AMARELO', 'PRETO', 'BRANCO', 'VERNIZ'],
        color: 'bg-blue-500'
      }
    }
  },
  'SUBLIMAÇÃO': {
    categories: {
      'PAPEL': {
        name: 'PAPEL',
        items: ['PAPEL TRATADO 64g', 'PAPEL MONOLÚCIDO 50g'],
        color: 'bg-orange-500'
      },
      'TINTAS': {
        name: 'TINTAS',
        items: ['CYAN', 'MAGENTA', 'AMARELO', 'PRETO'],
        color: 'bg-blue-500'
      }
    }
  },
  'ADESIVOS ROLAND': {
    categories: {
      'ADESIVOS': {
        name: 'ADESIVOS',
        items: ['BRANCO', 'TRANSPARENTE', 'CASCA DE OVO'],
        color: 'bg-emerald-500'
      },
      'TINTAS': {
        name: 'TINTAS',
        items: ['CYAN', 'MAGENTA', 'AMARELO', 'PRETO', 'VERNIZ', 'BRANCO'],
        color: 'bg-blue-500'
      }
    }
  }
};

export function SupplyRequestForm() {
  const [selectedService, setSelectedService] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { apiOnline } = useConnectivity();

  const handleSubmit = async () => {
    if (!selectedService || !selectedCategory || !selectedItem || !quantity) return;

    setIsSubmitting(true);

    try {
      corelCommunication.sendActionGuarded('solicitarInsumos', {
        servico: selectedService,
        categoria: selectedCategory,
        item: selectedItem,
        quantidade: parseInt(quantity),
        observacoes: notes
      } as any);
    } catch {}

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsSubmitting(false);
  };

  const currentService = selectedService ? supplies[selectedService] : null;
  const currentCategory = selectedCategory && currentService ? 
    currentService.categories[selectedCategory] : null;

  return (
    <div className="bg-navy-800 rounded-lg w-full max-w-xl mx-auto">
      <div className="p-6 space-y-6">
        {/* Service Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Serviço
          </label>
          <div className="grid grid-cols-2 gap-2">
            {Object.keys(supplies).map((service) => (
              <button
                key={service}
                onClick={() => {
                  setSelectedService(service);
                  setSelectedCategory('');
                  setSelectedItem('');
                }}
                className={`p-3 rounded-lg text-left transition-colors ${
                  selectedService === service
                    ? 'bg-navy-700 ring-1 ring-red-400'
                    : 'bg-navy-900 hover:bg-navy-700'
                }`}
              >
                <span className="text-white font-medium">{service}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Category Selection */}
        {selectedService && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Categoria
            </label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(supplies[selectedService].categories).map(([id, category]) => (
                <button
                  key={id}
                  onClick={() => {
                    setSelectedCategory(id);
                    setSelectedItem('');
                  }}
                  className={`p-3 rounded-lg text-left transition-colors ${
                    selectedCategory === id
                      ? 'bg-navy-700 ring-1 ring-red-400'
                      : 'bg-navy-900 hover:bg-navy-700'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full ${category.color} mr-2`} />
                    <span className="text-white font-medium">{category.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Item Selection */}
        {currentCategory && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Item
            </label>
            <div className="grid grid-cols-2 gap-2">
              {currentCategory.items.map((item) => (
                <button
                  key={item}
                  onClick={() => setSelectedItem(item)}
                  className={`p-3 rounded-lg text-left transition-colors ${
                    selectedItem === item
                      ? 'bg-navy-700 ring-1 ring-red-400'
                      : 'bg-navy-900 hover:bg-navy-700'
                  }`}
                >
                  <span className="text-white font-medium">{item}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quantity Input */}
        {selectedItem && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Quantidade
            </label>
            <div className="flex items-center">
              <button
                onClick={() => setQuantity(prev => Math.max(1, parseInt(prev) - 1).toString())}
                className="px-3 py-2 bg-navy-900 text-white rounded-l hover:bg-navy-700 transition-colors"
              >
                -
              </button>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-20 px-3 py-2 bg-navy-900 text-white text-center border-x border-navy-700 focus:outline-none"
              />
              <button
                onClick={() => setQuantity(prev => (parseInt(prev) + 1).toString())}
                className="px-3 py-2 bg-navy-900 text-white rounded-r hover:bg-navy-700 transition-colors"
              >
                // removed stray '+'
              </button>
              <span className="ml-3 text-gray-400">unidade</span>
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Observações
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 py-2 bg-navy-900 rounded text-white focus:outline-none focus:ring-1 focus:ring-red-400/50 resize-none"
            rows={3}
            placeholder="Descreva detalhes adicionais se necessário"
          />
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-navy-700">
        <div className="flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={!apiOnline || !selectedService || !selectedCategory || !selectedItem || !quantity || isSubmitting}
            title={!apiOnline ? 'Servidor offline' : undefined}
            className="flex items-center px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <PackagePlus className="w-4 h-4 mr-2" />
                Enviar Solicitação
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}